import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/auth';
import { api } from '../services/apiClient';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user profile from backend
  const fetchUserProfile = async () => {
    try {
      const profile = await api.users.getProfile();
      setUserProfile(profile);
      return profile;
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching user profile:', err);
      }
      setUserProfile(null);
      return null;
    }
  };

  // Register user
  const register = async (formData) => {
    try {
      setError(null);
      setLoading(true);
      
      // Register on backend (which creates Firebase Auth user)
      const profile = await api.users.register(formData);
      
      // Login with the credentials
      const { email, password } = Object.fromEntries(formData);
      await authService.login(email, password);
      
      return { success: true, profile };
    } catch (err) {
      // Transform errors into user-friendly messages
      let errorMessage = 'Registration failed, please try again';
      
      // Handle backend API errors
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === 'string') {
          if (detail.toLowerCase().includes('email') && detail.toLowerCase().includes('already')) {
            errorMessage = 'This email is already registered';
          } else if (detail.toLowerCase().includes('password')) {
            errorMessage = 'Password does not meet requirements';
          } else {
            errorMessage = detail;
          }
        }
      } 
      // Handle Firebase errors
      else if (err.code) {
        switch (err.code) {
          case 'auth/email-already-in-use':
            errorMessage = 'This email is already registered';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Invalid email address';
            break;
          case 'auth/weak-password':
            errorMessage = 'Password is too weak. Please use a stronger password';
            break;
          case 'auth/operation-not-allowed':
            errorMessage = 'Registration is currently disabled';
            break;
          default:
            errorMessage = 'Registration failed, please try again';
        }
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Login user
  const login = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      
      await authService.login(email, password);
      
      return { success: true };
    } catch (err) {
      // Transform Firebase errors into user-friendly messages
      let errorMessage = 'Failed to login, please try again';
      
      if (err.code) {
        switch (err.code) {
          case 'auth/invalid-credential':
          case 'auth/wrong-password':
          case 'auth/user-not-found':
            errorMessage = 'Failed to login, please try again';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Invalid email address';
            break;
          case 'auth/user-disabled':
            errorMessage = 'This account has been disabled';
            break;
          case 'auth/too-many-requests':
            errorMessage = 'Too many failed attempts. Please try again later';
            break;
          default:
            errorMessage = 'Failed to login, please try again';
        }
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Logout user
  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      setUserProfile(null);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Logout error:', err);
      }
    }
  };

  // Update user profile
  const updateProfile = async (data) => {
    try {
      const updatedProfile = await api.users.updateProfile(data);
      setUserProfile(updatedProfile);
      return { success: true, profile: updatedProfile };
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Update failed';
      return { success: false, error: errorMessage };
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      setError(null);
      await authService.resetPassword(email);
      return { success: true };
    } catch (err) {
      let errorMessage = 'Failed to send reset email';
      
      if (err.code) {
        switch (err.code) {
          case 'auth/invalid-email':
            errorMessage = 'Invalid email address';
            break;
          case 'auth/user-not-found':
            errorMessage = 'No account found with this email';
            break;
          case 'auth/too-many-requests':
            errorMessage = 'Too many requests. Please try again later';
            break;
          default:
            errorMessage = 'Failed to send reset email';
        }
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged(async (firebaseUser) => {
      setUser(firebaseUser);
      
      if (firebaseUser) {
        // Fetch user profile from backend
        await fetchUserProfile();
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    user,
    userProfile,
    loading,
    error,
    register,
    login,
    logout,
    updateProfile,
    resetPassword,
    fetchUserProfile,
    isAuthenticated: !!user,
    isApproved: userProfile?.approved || false,
    isAdmin: userProfile?.role?.toLowerCase() === 'admin',
    pricingTier: userProfile?.pricing_tier || 'retail',
  };

  // Debug logging for admin access issues (development only)
  if (process.env.NODE_ENV === 'development' && user && userProfile) {
    console.log('Auth Debug - User Profile:', {
      email: userProfile.email,
      role: userProfile.role,
      isAdmin: userProfile?.role?.toLowerCase() === 'admin',
      approved: userProfile.approved,
    });
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

